import { Text } from '@thanx/uikit/text'
import Book from 'components/Icons/Book'
import Bulb from 'components/Icons/Bulb'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import SectionShell from './SectionShell'

const t = buildTranslate('recovery_ai.promo.help')

// Figma 129:1840 (help footer): grey05 field, 48/64 vertical padding on a 48px
// gutter, contents capped to a 1080px centered column.
const SECTION_PADDING = '48px 48px 64px'
const CONTENT_MAX_WIDTH = '1080px'
// Figma pairs every label with its icon at a 4px gap; the two link rows sit 12px
// apart, 16px below the "Help" heading.
const LABEL_ICON_GAP = '4px'
const LINK_ROW_GAP = '12px'
const LINKS_OFFSET_TOP = '16px'
// Both the heading bulb and the per-link book render in a 12x14 box.
const ICON_WIDTH = '12px'
const ICON_HEIGHT = '14px'

type HelpLinkKey = 'how_it_works' | 'faq'

export type HelpUrls = Record<HelpLinkKey, string>

/** Render order of the two link rows, per Figma. */
const HELP_LINK_KEYS: HelpLinkKey[] = ['how_it_works', 'faq']

// TODO: no RecoveryAI help-centre article or FAQ exists in `utilities/urlUtils`
// yet (the Figma links are unresolved too), and shipping `href="#"` rows would
// put dead links in production. Populate this with the real help.thanx.com URLs
// to un-hide the section — no other edit is needed.
const HELP_URLS: HelpUrls | null = null

/**
 * The designed help footer. Split from the gate below so the layout is exercised
 * by tests (and trivially restored) while `HELP_URLS` is still unpublished.
 */
export const HelpSectionContent = ({ urls }: { urls: HelpUrls }) => {
  const [css] = useStyletron()

  // The shared icon components carry their own intrinsic width/height, so the
  // designed 12x14 box lives on the wrapper (which also owns the decorative
  // aria-hidden, since neither icon sets it) and the glyph fills it.
  const iconBoxClass = css({
    display: 'inline-flex',
    flexShrink: 0,
    height: ICON_HEIGHT,
    width: ICON_WIDTH,
  })
  const iconGlyphClass = css({ height: '100%', width: '100%' })

  const rowClass = css({
    alignItems: 'center',
    display: 'flex',
    gap: LABEL_ICON_GAP,
  })

  return (
    <SectionShell
      backgroundColor={merchantTheme.colors.grey05}
      contentMaxWidth={CONTENT_MAX_WIDTH}
      padding={SECTION_PADDING}
    >
      <div className={rowClass}>
        {/* Bulb paints its own path grey70 (a hardcoded fill in the shared icon),
              a shade lighter than the Figma grey90 lightbulb — recolouring the
              shared icon is out of scope for this page. */}
        <span aria-hidden="true" className={iconBoxClass}>
          <Bulb className={iconGlyphClass} />
        </span>
        <Text.BodyText3 tag="h2" bold>
          {t('title')}
        </Text.BodyText3>
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: LINK_ROW_GAP,
          paddingTop: LINKS_OFFSET_TOP,
        })}
      >
        {HELP_LINK_KEYS.map(key => (
          <a
            key={key}
            className={rowClass}
            href={urls[key]}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text.BodyText5 color="belizeHole50">{t(key)}</Text.BodyText5>
            <span aria-hidden="true" className={iconBoxClass}>
              <Book
                className={iconGlyphClass}
                fillColor={merchantTheme.colors.belizeHole50}
              />
            </span>
          </a>
        ))}
      </div>
    </SectionShell>
  )
}

const HelpSection = () =>
  HELP_URLS ? <HelpSectionContent urls={HELP_URLS} /> : null

export default HelpSection
